import { ChangeDetectionStrategy, Component, ElementRef, ViewChild, signal, inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { CommonModule, DecimalPipe } from "@angular/common";
import { Observable } from "rxjs";

import { IImportResult } from "../../models/import-result.interface";

export interface ImportCsvDialogData {
    onFileSelected: (file: File) => Observable<IImportResult>;
}

@Component({
    selector: 'import-csv-dialog',
    standalone: true,
    templateUrl: './import-csv.dialog.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatProgressBarModule,
        DecimalPipe
    ]
})
export class ImportCsvDialog {
    @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

    private readonly _dialogRef = inject(MatDialogRef<ImportCsvDialog>);
    private readonly _data = inject<ImportCsvDialogData>(MAT_DIALOG_DATA);

    readonly isDragging = signal(false);
    readonly selectedFile = signal<File | null>(null);
    readonly isUploading = signal(false);

    onDragOver(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.isDragging.set(true);
    }

    onDragLeave(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.isDragging.set(false);
    }

    onDrop(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.isDragging.set(false);

        const files = event.dataTransfer?.files;
        if (files && files.length > 0) {
            this.handleFile(files[0]);
        }
    }

    onFileInputChange(event: Event) {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            this.handleFile(input.files[0]);
        }
    }

    private handleFile(file: File) {
        if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
            this.selectedFile.set(file);
        } else {
            alert('Por favor, selecione um arquivo CSV válido.');
        }
    }

    openFileSelector() {
        this.fileInput.nativeElement.click();
    }

    removeFile() {
        this.selectedFile.set(null);
        if (this.fileInput.nativeElement) {
            this.fileInput.nativeElement.value = '';
        }
    }

    downloadTemplate() {
        const csvContent = this.generateTemplateCsv();
        const BOM = '\uFEFF';
        // Usa tipo específico para CSV
        const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        
        link.setAttribute('href', url);
        link.setAttribute('download', 'template-importacao-usuarios.csv');
        link.style.visibility = 'hidden';
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    private generateTemplateCsv(): string {
        // Todos os campos são separados por ponto e vírgula
        const header = 'nome;email;senha;telefone;clientName;applicationName;roleName;groupId;cpf;dataExpiracao;createInProtenet';
        
        // Exemplos de dados
        const examples = [
            'João Silva;joao.silva@example.com;Senha1234!;11987654321;UNE;bridgefy;public;1;12345678901;31/12/2025;true',
            'Maria Santos;maria.santos@example.com;Senha5678!;11987654322;DGT - Privado;bridgefy;audit;;98765432100;;false',
            'Pedro Costa;pedro.costa@example.com;Senha9012!;11987654323;teste em lote;bridgefy;superuser;512512222;49134529034;01/11/2025;true'
        ];
        
        // Junta cabeçalho e exemplos com quebra de linha Windows (\r\n)
        return [header, ...examples].join('\r\n');
    }

    onImportClick() {
        const file = this.selectedFile();
        if (file && this._data?.onFileSelected) {
            this.isUploading.set(true);
            this._data.onFileSelected(file).subscribe({
                next: (result) => {
                    this.isUploading.set(false);
                    this._dialogRef.close(result);
                },
                error: () => {
                    this.isUploading.set(false);
                }
            });
        }
    }

    onCancelClick() {
        this._dialogRef.close(false);
    }
}

